import { useState } from 'react'
import Modal from '@/components/common/Modal'
import { Upload, FileSpreadsheet, AlertCircle, CheckCircle2 } from 'lucide-react'
import toast from 'react-hot-toast'

export default function ImportModal({ open, onClose, title, onImport, downloadTemplateUrl, onDownloadTemplate }) {
    const [file, setFile] = useState(null)
    const [loading, setLoading] = useState(false)
    const [result, setResult] = useState(null)

    const handleClose = () => {
        if (!loading) {
            setFile(null)
            setResult(null)
            onClose()
        }
    }

    const handleFileChange = (e) => {
        const selected = e.target.files[0]
        if (selected && (selected.name.endsWith('.xlsx') || selected.name.endsWith('.xls'))) {
            setFile(selected)
            setResult(null)
        } else {
            toast.error('Допустимы только файлы Excel (.xlsx, .xls)')
        }
    }

    const handleSubmit = async () => {
        if (!file) return
        setLoading(true)
        setResult(null)
        try {
            const resp = await onImport(file)
            // Expecting resp.data to have { created: int, errors: array }
            setResult({
                success: true,
                created: resp.data.created || 0,
                errors: resp.data.errors || []
            })
            if (resp.data.errors?.length === 0) {
                toast.success(`Успешно импортировано: ${resp.data.created}`)
            } else {
                toast('Импорт завершен с ошибками', { icon: '⚠️' })
            }
        } catch (err) {
            setResult({
                success: false,
                message: err.response?.data?.error || 'Ошибка загрузки файла'
            })
            toast.error('Ошибка импорта')
        } finally {
            setLoading(false)
        }
    }

    return (
        <Modal
            open={open}
            onClose={handleClose}
            title={title || "Импорт данных"}
            size="md"
            footer={
                result?.success && result?.errors?.length === 0 ? (
                    <button className="btn-primary" onClick={handleClose}>Готово</button>
                ) : (
                    <>
                        <button className="btn-secondary" onClick={handleClose} disabled={loading}>
                            Отмена
                        </button>
                        <button
                            className="btn-primary"
                            onClick={handleSubmit}
                            disabled={!file || loading || result?.success}
                        >
                            {loading ? 'Загрузка...' : 'Импортировать'}
                        </button>
                    </>
                )
            }
        >
            <div className="space-y-4">
                {(downloadTemplateUrl || onDownloadTemplate) && (
                    <div className="text-right">
                        {onDownloadTemplate ? (
                            <button
                                onClick={onDownloadTemplate}
                                className="text-sm text-primary-600 hover:underline flex items-center gap-1 justify-end ml-auto"
                            >
                                <FileSpreadsheet size={16} /> Скачать шаблон Excel
                            </button>
                        ) : (
                            <a href={downloadTemplateUrl} download className="text-sm text-primary-600 hover:underline flex items-center gap-1 justify-end">
                                <FileSpreadsheet size={16} /> Скачать шаблон Excel
                            </a>
                        )}
                    </div>
                )}

                {/* Upload State */}
                {!result?.success && (
                    <div className="border-2 border-dashed border-gray-300 rounded-xl p-6 flex flex-col items-center justify-center bg-gray-50 hover:bg-gray-100 transition-colors">
                        <Upload size={32} className="text-gray-400 mb-3" />
                        <p className="text-sm font-medium text-gray-700 mb-1">
                            {file ? file.name : "Выберите файл Excel"}
                        </p>
                        <p className="text-xs text-gray-500 mb-4">.xlsx, .xls до 5MB</p>
                        <label className="btn-secondary cursor-pointer">
                            Выбрать файл
                            <input type="file" accept=".xlsx,.xls" className="hidden" onChange={handleFileChange} />
                        </label>
                    </div>
                )}

                {/* Error / Result State */}
                {result && (
                    <div className={`p-4 rounded-xl ${result.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                        <div className="flex items-center gap-2 mb-2 font-medium">
                            {result.success ? (
                                <CheckCircle2 className="text-green-600" size={18} />
                            ) : (
                                <AlertCircle className="text-red-600" size={18} />
                            )}
                            <span className={result.success ? 'text-green-800' : 'text-red-800'}>
                                {result.success ? 'Результат импорта' : 'Ошибка импорта'}
                            </span>
                        </div>

                        {result.success ? (
                            <div className="space-y-2 text-sm text-green-700">
                                <p>Успешно создано: <strong>{result.created}</strong> записей</p>
                                {result.errors?.length > 0 && (
                                    <div className="mt-3">
                                        <p className="font-semibold text-red-600 mb-1">Ошибки ({result.errors.length}):</p>
                                        <ul className="text-xs text-red-500 list-disc list-inside space-y-1 max-h-32 overflow-y-auto">
                                            {result.errors.map((err, i) => (
                                                <li key={i}>{err}</li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <p className="text-sm text-red-600">{result.message}</p>
                        )}
                    </div>
                )}
            </div>
        </Modal>
    )
}
